import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import { useDeviceType } from '../../hooks/useDeviceType';

const Header = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isMobile } = useDeviceType();

  // 检测屏幕大小变化
  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('header')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const headerStyle = {
    background: colors.headerBackground,
    backdropFilter: 'blur(10px)',
    padding: '1rem 2rem',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
    borderBottom: `1px solid ${colors.border}`,
    position: isMobile ? 'relative' : 'sticky',
    top: isMobile ? 'auto' : 0,
    zIndex: 100,
    width: '100%'
  };

  const navStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const logoStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: colors.headerText,
    textDecoration: 'none'
  };

  // Mobile menu - 部分覆盖设计
  const mobileMenuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: colors.headerBackground,
    backdropFilter: 'blur(15px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    display: isMobileMenuOpen ? 'flex' : 'none',
    flexDirection: 'column',
    padding: '1.5rem 2rem',
    gap: '1rem',
    zIndex: 999,
    borderBottom: `1px solid ${colors.border}`,
    transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
    opacity: isMobileMenuOpen ? 1 : 0,
    transition: 'all 0.3s ease'
  };

  const linkStyle = (isActive) => ({
    textDecoration: 'none',
    color: isActive ? colors.link : colors.textSecondary,
    fontWeight: isActive ? '600' : '400',
    padding: '0.8rem 1.2rem',
    borderRadius: '10px',
    transition: 'all 0.3s ease',
    backgroundColor: isActive ? `${colors.link}20` : 'transparent',
    display: 'block',
    textAlign: 'center',
    fontSize: '1rem',
    border: isActive ? `2px solid ${colors.link}40` : '2px solid transparent'
  });

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header style={headerStyle}>
      <nav style={navStyle}>
        <Link to="/" style={logoStyle} onClick={closeMobileMenu}>
          {t('header.title')}
        </Link>
        
        {/* Desktop Navigation */}
        <div style={{
          display: isMobile ? 'none' : 'flex',
          gap: '2rem',
          alignItems: 'center'
        }}>
          <Link 
            to="/" 
            style={linkStyle(location.pathname === '/')}
          >
            {t('header.ipInfo')}
          </Link>
          <Link 
            to="/dns" 
            style={linkStyle(location.pathname === '/dns')}
          >
            {t('header.dnsLookup')}
          </Link>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button 
          style={{
            display: isMobile ? 'block' : 'none',
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: colors.headerText,
            zIndex: 1001,
            position: 'relative',
            padding: '0.5rem',
            borderRadius: '8px',
            transition: 'all 0.3s ease'
          }}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </nav>

      {/* Mobile Menu - 部分覆盖 */}
      <div style={mobileMenuStyle}>
        <Link 
          to="/" 
          style={linkStyle(location.pathname === '/')}
          onClick={closeMobileMenu}
        >
          {t('header.ipInfo')}
        </Link>
        <Link 
          to="/dns" 
          style={linkStyle(location.pathname === '/dns')}
          onClick={closeMobileMenu}
        >
          {t('header.dnsLookup')}
        </Link>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          flexDirection: 'column',
          gap: '1rem',
          marginTop: '1rem',
          padding: '1rem 0',
          borderTop: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ThemeSwitcher />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
